import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AuthGateway } from '../../application/AuthGateway'
import {
  AuthFailure,
  permissionKeys,
  roleKeys,
  type AppSession,
  type LoginCredentials,
  type PermissionKey,
  type RoleKey,
} from '../../domain/auth'

/**
 * Les rôles et permissions sont lus en texte libre, puis **filtrés** sur ce que
 * cette version du client sait faire.
 *
 * Un enum strict paraissait plus sûr : il ne l'était pas. La base peut recevoir
 * une permission nouvelle avant que le navigateur ne recharge le bundle qui la
 * connaît. Avec un enum, cette avance normale rendait la session illisible et
 * fermait la porte à des comptes parfaitement valides. Ignorer l'inconnu
 * n'accorde jamais rien : un droit non reconnu reste un droit non exercé.
 */
const knownRoles = new Set<string>(roleKeys)
const knownPermissions = new Set<string>(permissionKeys)

const appSessionSchema = z.object({
  authUserId: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  displayName: z.string(),
  email: z.string(),
  status: z.enum([
    'INVITED',
    'ACTIVE',
    'SUSPENDED',
    'DISABLED',
    'ARCHIVED',
    'PASSWORD_CHANGE_REQUIRED',
    'SESSION_REVOKED',
  ]),
  roles: z
    .array(z.string())
    .transform((values) => values.filter((value): value is RoleKey => knownRoles.has(value))),
  permissions: z
    .array(z.string())
    .transform((values) => values.filter((value): value is PermissionKey => knownPermissions.has(value))),
  employeeId: z.string().nullable().optional(),
  canOperate: z.boolean(),
})

async function resolveAppSession(client: SupabaseClient): Promise<AppSession | null> {
  const response = (await client.rpc('current_app_session')) as unknown as {
    data: unknown
    error: unknown
  }
  const { data, error } = response
  if (error) {
    throw new AuthFailure('ACCOUNT_SETUP_REQUIRED', 'Votre profil n’est pas encore configuré.')
  }
  if (data === null) return null
  const parsed = appSessionSchema.safeParse(data)
  // Un profil illisible n'est pas un compte suspendu : le dire évite d'envoyer
  // l'utilisateur, et celui qui l'assiste, chercher une sanction inexistante.
  if (!parsed.success) {
    throw new AuthFailure(
      'UNEXPECTED',
      'Votre profil n’a pas pu être lu. Rechargez la page; si le message revient, prévenez l’administrateur.',
    )
  }
  const { status } = parsed.data
  // Compte désactivé ou sessions révoquées par un administrateur : la session locale est fermée, l'accès serveur
  // l'est déjà.
  if (status === 'SESSION_REVOKED') {
    await client.auth.signOut({ scope: 'local' })
    throw new AuthFailure(
      'SESSION_EXPIRED',
      'Votre session a été fermée par un administrateur. Reconnectez-vous.',
    )
  }
  if (status === 'DISABLED') {
    await client.auth.signOut({ scope: 'local' })
    throw new AuthFailure('USER_INACTIVE', 'Ce compte est désactivé. Adressez-vous à un administrateur.')
  }
  if (status !== 'ACTIVE' && status !== 'PASSWORD_CHANGE_REQUIRED') {
    throw new AuthFailure('USER_INACTIVE', 'Votre accès est temporairement suspendu.')
  }
  const { employeeId, ...session } = { ...parsed.data, status }
  return employeeId ? { ...session, employeeId } : session
}

function browserLabel(): string | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.userAgent.slice(0, 300)
}

/** Code `{ error: 'CODE' }` renvoyé par la fonction Edge `employee-accounts`. */
async function functionErrorCode(error: unknown): Promise<string | undefined> {
  const context = (error as { context?: unknown } | null)?.context
  if (!(context instanceof Response)) return undefined
  const body = (await context.json().catch(() => null)) as { error?: unknown } | null
  return typeof body?.error === 'string' ? body.error : undefined
}

const replacementFailures: Record<string, string> = {
  CURRENT_PASSWORD_INVALID: 'Le mot de passe temporaire saisi est incorrect.',
  WEAK_PASSWORD:
    'Choisissez au moins 12 caractères, avec trois types parmi minuscules, majuscules, chiffres, symboles.',
  PASSWORD_UNCHANGED: 'Le nouveau mot de passe doit être différent du mot de passe temporaire.',
  PASSWORD_CHANGE_NOT_REQUIRED: 'Votre mot de passe a déjà été remplacé. Reconnectez-vous.',
}

export function createSupabaseAuthGateway(client: SupabaseClient): AuthGateway {
  const audit = async (eventType: 'LOGIN_SUCCEEDED' | 'LOGOUT' | 'PASSWORD_CHANGED') => {
    const userAgent = browserLabel()
    await client.rpc('record_security_event', {
      event_type: eventType,
      ...(userAgent ? { event_metadata: { userAgent } } : {}),
    })
  }
  // Le fournisseur ne garde pas de trace exploitable des refus : l'écran de connexion les signale. Le serveur ne
  // rattache la tentative à un compte que si l'identifiant saisi correspond exactement, et ne dit jamais lequel.
  const recordFailure = async (email: string) => {
    try {
      await client.rpc('record_login_failure_v1', {
        p_identifier: email,
        p_user_agent: browserLabel() ?? null,
      })
    } catch {
      // Le signalement ne doit jamais masquer le message de connexion refusée.
    }
  }
  return {
    async getSession() {
      const { data } = await client.auth.getSession()
      return data.session ? resolveAppSession(client) : null
    },
    async signIn({ email, password }: LoginCredentials) {
      const { data, error } = await client.auth.signInWithPassword({ email, password })
      // Tout échec passait pour un mot de passe erroné. Une limite de débit ou
      // une panne réseau invite alors à réessayer, ce qui aggrave le blocage.
      if (error?.status === 429) {
        throw new AuthFailure(
          'UNEXPECTED',
          'Trop de tentatives depuis ce réseau. Patientez quelques minutes avant de réessayer.',
        )
      }
      if (error && error.status !== 400) {
        throw new AuthFailure('UNEXPECTED', 'La connexion au service a échoué. Réessayez dans un instant.')
      }
      if (!data.session) {
        await recordFailure(email)
        throw new AuthFailure('INVALID_CREDENTIALS', 'Courriel ou mot de passe invalide.')
      }
      try {
        const session = await resolveAppSession(client)
        if (!session)
          throw new AuthFailure('ACCOUNT_SETUP_REQUIRED', 'Votre profil n’est pas encore configuré.')
        await audit('LOGIN_SUCCEEDED')
        return session
      } catch (error) {
        await client.auth.signOut()
        throw error
      }
    },
    async signOut() {
      await audit('LOGOUT')
      const { error } = await client.auth.signOut()
      if (error) throw new AuthFailure('UNEXPECTED', 'La déconnexion n’a pas pu être terminée.')
    },
    async requestPasswordReset(email, redirectUrl) {
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl })
      if (error) throw new AuthFailure('UNEXPECTED', 'La demande ne peut pas être traitée pour le moment.')
    },
    async updatePassword(password) {
      const { error } = await client.auth.updateUser({ password })
      if (error) throw new AuthFailure('UNEXPECTED', 'Le mot de passe n’a pas pu être modifié.')
      await audit('PASSWORD_CHANGED')
    },
    async replaceTemporaryPassword(currentPassword, newPassword) {
      const { data: sessionData } = await client.auth.getSession()
      const email = sessionData.session?.user.email
      if (!email) throw new AuthFailure('SESSION_EXPIRED', 'Votre session a expiré. Reconnectez-vous.')
      const invocation = await client.functions.invoke('employee-accounts', {
        body: { action: 'replace-temporary-password', currentPassword, newPassword },
      })
      if (invocation.error) {
        const code = await functionErrorCode(invocation.error)
        throw new AuthFailure(
          'UNEXPECTED',
          (code ? replacementFailures[code] : undefined) ??
            'Le mot de passe n’a pas pu être remplacé. Réessayez dans un instant.',
        )
      }
      // Toutes les sessions viennent d'être révoquées : une nouvelle s'ouvre avec le nouveau mot de passe.
      await client.auth.signOut({ scope: 'local' })
      const { error } = await client.auth.signInWithPassword({ email, password: newPassword })
      if (error) {
        throw new AuthFailure('SESSION_EXPIRED', 'Mot de passe remplacé. Reconnectez-vous avec le nouveau.')
      }
      const session = await resolveAppSession(client)
      if (!session)
        throw new AuthFailure('ACCOUNT_SETUP_REQUIRED', 'Votre profil n’est pas encore configuré.')
      await audit('LOGIN_SUCCEEDED')
      return session
    },
    subscribe(listener) {
      const { data } = client.auth.onAuthStateChange(() => {
        listener()
      })
      return () => {
        data.subscription.unsubscribe()
      }
    },
  }
}
